import React from 'react';
import {ConstraintDataPlugin, ConstraintPluginProps} from '../ConstraintDataPlugin';
import {
    ConstraintType,
    DataTypeConstraintDto,
    TimeRangeConstraintDataDto
} from '../../../../../../common/apis/datatype/DataTypeConstraintDto';
import {CoreDataType} from "../../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {DataTypeTranslationKey} from "../../DataTypeTranslationKey";
import {TimeRangeConstraintPluginForm} from "./TimeRangeConstraintPluginForm";
import {TimeFormatsConverter} from "./TimeFormatsConverter";
import {ConstraintValueValidationPlugin} from "../ConstraintValueValidationPlugin";
import {TranslationFunction} from "../../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../../CommonTranslation";
import {StereotypesTranslationKey} from "../../../stereotypes/StereotypesTranslationKey";
import {DateTimeFormatter} from "../../../../../../common/DateTimeFormatter";

export class TimeRangeConstraintPlugin implements ConstraintDataPlugin<TimeRangeConstraintDataDto>, ConstraintValueValidationPlugin {

    readonly type = ConstraintType.TIME_RANGE;

    getSupportedDataTypes(): CoreDataType[] {
        return [
            CoreDataType.TIME
        ];
    }

    getInitialData(): TimeRangeConstraintDataDto {
        return {
            type: ConstraintType.TIME_RANGE,
            timeFrom: "00:00:00",
            timeTo: "23:59:00"
        };
    }

    render(props: ConstraintPluginProps<TimeRangeConstraintDataDto>): React.ReactElement {
        return <TimeRangeConstraintPluginForm {...props} />;
    }

    validate(data: TimeRangeConstraintDataDto, coreDataType: CoreDataType): Record<string, string> {
        const errors: Record<string, string> = {};
        let convertedTimeFrom = TimeFormatsConverter.stringTimeToDayjs(data.timeFrom);
        let convertedTimeTo = TimeFormatsConverter.stringTimeToDayjs(data.timeTo);

        if (data.timeFrom != null && convertedTimeFrom === null) {
            errors.timeFrom = DataTypeTranslationKey.INVALID_TIME_FROM;
        }
        if (data.timeTo != null && convertedTimeTo === null) {
            errors.timeTo = DataTypeTranslationKey.INVALID_TIME_TO;
        }
        if (!data.timeFrom && !data.timeTo) {
            errors.timeFrom = DataTypeTranslationKey.AT_LEAST_ONE_OF_TIME_FROM_OR_TIME_TO_MUST_BE_SPECIFIED;
        } else if (convertedTimeFrom && convertedTimeTo?.isBefore(convertedTimeFrom)) {
            errors.timeFrom = DataTypeTranslationKey.TIME_FROM_MUST_BE_BEFORE_TIME_TO;
        }

        return errors;
    }

    getConstraintExpression(data: TimeRangeConstraintDataDto): string {
        const {timeFrom, timeTo} = data;
        if (timeFrom === undefined && timeTo === undefined) {
            return "";
        }

        const left = DateTimeFormatter.formatTime(timeFrom) ?? "-∞";
        const right = DateTimeFormatter.formatTime(timeTo) ?? "∞";

        return `<${left} - ${right}>`;
    }

    validateValue(value: any, attributeId: string, constraint: DataTypeConstraintDto, t: TranslationFunction): Record<string, string> {
        const constraintData = constraint.constraintData as TimeRangeConstraintDataDto;
        const {timeFrom, timeTo} = constraintData;

        if (!value) {
            return {
                [attributeId]: t(CommonTranslation.FILL_OUT_THIS_FIELD)
            };
        }

        const convertedValue = TimeFormatsConverter.stringTimeToDayjs(value);
        if (!convertedValue) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_NOT_VALID_TIME), attributeId);
        }

        const convertedTimeFrom = TimeFormatsConverter.stringTimeToDayjs(timeFrom);
        const convertedTimeTo = TimeFormatsConverter.stringTimeToDayjs(timeTo);

        if (convertedValue?.isBefore(convertedTimeFrom)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_BELLOW_ALLOWED_LIMIT), attributeId);
        }

        if (convertedValue.isAfter(convertedTimeTo)) {
            return this.errorResult(t(StereotypesTranslationKey.VALUE_IS_ABOVE_ALLOWED_LIMIT), attributeId);
        }

        return {};
    }

    private errorResult(error: string, attributeId: string) {
        return {
            [attributeId]: error
        };
    }
}
